/* Tesla Industries theme — bag, product, media and page features.
   Loaded after theme.js, which provides the helpers and the boot sequence. */
(() => {
  'use strict';

  const { $, $$, debounce, announce, formatMoney, prefersReducedMotion } = window.Theme;
  const theme = window.theme || { routes: {}, strings: {} };

  /* ==========================================================
     Bag (cart) — counts, add to bag, confirmation popover
     ========================================================== */
  const updateBagCount = (count) => {
    $$('[data-bag-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    });
    $$('[data-bag-count-text]').forEach((el) => { el.textContent = count; });
    $$('[data-bag-link]').forEach((el) => {
      const base = el.getAttribute('aria-label') || '';
      el.setAttribute('aria-label', base.replace(/\d+/, count));
    });
  };

  const bagPop = {
    el: null,
    timer: null,
    lastFocus: null,
    open(item) {
      this.el = this.el || document.getElementById('BagPop');
      if (!this.el) return;
      const img = $('[data-bag-pop-image]', this.el);
      const src = item.featured_image?.url || item.image;
      if (src) {
        img.src = `${src}${src.includes('?') ? '&' : '?'}width=160`;
        img.alt = item.featured_image?.alt || '';
        img.hidden = false;
      } else {
        img.hidden = true;
      }
      $('[data-bag-pop-title]', this.el).textContent = item.product_title;
      $('[data-bag-pop-variant]', this.el).textContent =
        item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      this.lastFocus = document.activeElement;
      this.el.hidden = false;
      requestAnimationFrame(() => this.el.classList.add('is-open'));
      window.setTimeout(() => $('a, button:not([data-bag-pop-close])', this.el)?.focus(), 120);
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => {
        if (!this.el.contains(document.activeElement)) this.close();
      }, 8000);
    },
    close() {
      if (!this.el || this.el.hidden) return;
      this.el.classList.remove('is-open');
      window.setTimeout(() => { this.el.hidden = true; }, 300);
      if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus();
    },
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-bag-pop-close]')) bagPop.close();
    else if (bagPop.el && !bagPop.el.hidden && !event.target.closest('#BagPop') && !event.target.closest('[data-product-form]')) bagPop.close();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') bagPop.close();
  });

  const initProductForms = (root = document) => {
    $$('form[data-product-form]', root).forEach((form) => {
      if (form.dataset.ready) return;
      form.dataset.ready = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = $('[type="submit"]', form);
        const errorEl = $('[data-form-error]', form);
        if (button?.disabled) return;
        button?.classList.add('is-loading');
        button?.setAttribute('aria-busy', 'true');
        if (errorEl) errorEl.hidden = true;
        try {
          const res = await fetch(`${theme.routes.cartAdd}.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: new FormData(form),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.description || data.message || theme.strings.addError);
          const cart = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
          updateBagCount(cart.item_count);
          bagPop.open(data.items ? data.items[0] : data);
          announce(theme.strings.added);
        } catch (err) {
          if (errorEl) {
            errorEl.textContent = err.message || theme.strings.addError;
            errorEl.hidden = false;
          }
          announce(err.message || theme.strings.addError);
        } finally {
          button?.classList.remove('is-loading');
          button?.removeAttribute('aria-busy');
        }
      });
    });
  };

  /* ==========================================================
     Product: variants, gallery, quantity
     ========================================================== */
  const initProduct = (root = document) => {
    $$('[data-product]', root).forEach((productEl) => {
      if (productEl.dataset.ready) return;
      productEl.dataset.ready = 'true';
      const jsonEl = $('[data-product-json]', productEl);
      if (!jsonEl) return;
      const product = JSON.parse(jsonEl.textContent);
      const form = $('form[data-product-form]', productEl);
      const idInput = form ? $('input[name="id"]', form) : null;
      const priceEl = $('[data-price]', productEl);
      const compareEl = $('[data-compare-price]', productEl);
      const submit = form ? $('[type="submit"]', form) : null;
      const submitLabel = submit ? $('[data-label]', submit) : null;
      const gallery = $('[data-product-gallery]', productEl);

      const selectedOptions = () =>
        $$('[data-option-index]', productEl).map((fieldset) => {
          const checked = $('input:checked', fieldset);
          return checked ? checked.value : null;
        });

      const update = () => {
        const options = selectedOptions();
        const variant = product.variants.find((v) => v.options.every((opt, i) => opt === options[i]));
        if (!variant) {
          if (submit) {
            submit.disabled = true;
            if (submitLabel) submitLabel.textContent = theme.strings.unavailable;
          }
          return;
        }
        if (idInput) idInput.value = variant.id;
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (compareEl) {
          const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          compareEl.hidden = !onSale;
          if (onSale) compareEl.textContent = formatMoney(variant.compare_at_price);
        }
        if (submit) {
          submit.disabled = !variant.available;
          if (submitLabel) submitLabel.textContent = variant.available ? theme.strings.addToBag : theme.strings.soldOut;
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        if (variant.featured_media && gallery) {
          const index = $$('[data-media-id]', gallery).findIndex((el) => el.dataset.mediaId === String(variant.featured_media.id));
          if (index > -1) scrollGalleryTo(gallery, index);
        }
      };

      $$('[data-option-index] input', productEl).forEach((input) => input.addEventListener('change', update));
    });
  };

  const scrollGalleryTo = (gallery, index) => {
    const track = $('[data-gallery-main]', gallery);
    const item = $$('[data-media-id]', gallery)[index];
    if (!track || !item) return;
    track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
  };

  const initProductGallery = (root = document) => {
    $$('[data-product-gallery]', root).forEach((gallery) => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      const track = $('[data-gallery-main]', gallery);
      const thumbs = $$('[data-thumb-index]', gallery);
      const items = $$('[data-media-id]', gallery);

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => scrollGalleryTo(gallery, Number(thumb.dataset.thumbIndex)));
      });

      const sync = () => {
        const index = Math.round(track.scrollLeft / track.clientWidth);
        thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === index)));
        items.forEach((item, i) => {
          const video = $('video', item);
          if (video && i !== index && !video.paused) video.pause();
        });
      };
      track?.addEventListener('scroll', debounce(sync, 60), { passive: true });

      track?.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const current = Math.round(track.scrollLeft / track.clientWidth);
        const next = Math.max(0, Math.min(items.length - 1, current + (event.key === 'ArrowRight' ? 1 : -1)));
        scrollGalleryTo(gallery, next);
      });
    });
  };

  const initQuantity = (root = document) => {
    $$('[data-qty]', root).forEach((wrap) => {
      if (wrap.dataset.ready) return;
      wrap.dataset.ready = 'true';
      const input = $('input', wrap);
      const step = (delta) => {
        const min = Number(input.min || 0);
        const max = input.max ? Number(input.max) : Infinity;
        const value = Math.max(min, Math.min(max, (Number(input.value) || 0) + delta));
        if (value === Number(input.value)) return;
        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      $('[data-qty-minus]', wrap)?.addEventListener('click', () => step(-1));
      $('[data-qty-plus]', wrap)?.addEventListener('click', () => step(1));
    });
  };

  /* Cart page: submit the form when a quantity changes */
  const initCart = (root = document) => {
    const form = $('form[data-cart-form]', root);
    if (!form || form.dataset.ready) return;
    form.dataset.ready = 'true';
    const submitLater = debounce(() => {
      form.classList.add('is-updating');
      form.requestSubmit ? form.requestSubmit($('[name="update"]', form)) : form.submit();
    }, 450);
    $$('[data-cart-qty]', form).forEach((input) => input.addEventListener('change', submitLater));
  };

  /* ==========================================================
     Media: background video with pause control
     ========================================================== */
  const initVideos = (root = document) => {
    $$('[data-autoplay-video]', root).forEach((wrap) => {
      if (wrap.dataset.ready) return;
      wrap.dataset.ready = 'true';
      const video = $('video', wrap);
      const toggle = $('[data-media-toggle]', wrap);
      if (!video) return;
      let userPaused = prefersReducedMotion.matches;

      const setState = (playing) => {
        if (!toggle) return;
        toggle.setAttribute('aria-pressed', String(playing));
        const label = $('.visually-hidden', toggle);
        if (label) label.textContent = playing ? theme.strings.pause : theme.strings.play;
      };

      const play = () => {
        const p = video.play();
        if (p && p.catch) p.then(() => setState(true)).catch(() => setState(false));
        else setState(true);
      };

      toggle?.addEventListener('click', () => {
        if (video.paused) {
          userPaused = false;
          play();
        } else {
          userPaused = true;
          video.pause();
          setState(false);
        }
      });

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !userPaused) play();
            else if (!entry.isIntersecting && !video.paused) video.pause();
          });
        }, { threshold: 0.2 });
        io.observe(wrap);
      } else if (!userPaused) {
        play();
      }
      if (userPaused) setState(false);
    });
  };

  /* ==========================================================
     Gallery carousel (scroll snap)
     ========================================================== */
  const initGalleries = (root = document) => {
    $$('[data-gallery]', root).forEach((gallery) => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      const track = $('[data-gallery-track]', gallery);
      const prev = $('[data-gallery-prev]', gallery);
      const next = $('[data-gallery-next]', gallery);
      if (!track) return;

      const slideWidth = () => {
        const slide = track.firstElementChild;
        if (!slide) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return slide.getBoundingClientRect().width + gap;
      };

      const updateButtons = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      };

      const go = (dir) => track.scrollBy({ left: dir * slideWidth(), behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
      prev?.addEventListener('click', () => go(-1));
      next?.addEventListener('click', () => go(1));
      track.addEventListener('scroll', debounce(updateButtons, 50), { passive: true });
      window.addEventListener('resize', debounce(updateButtons, 150));
      updateButtons();
    });
  };

  /* ==========================================================
     Local navigation: highlight the section in view
     ========================================================== */
  const initLocalNav = (root = document) => {
    const nav = $('[data-local-nav]', root);
    if (!nav || nav.dataset.ready || !('IntersectionObserver' in window)) return;
    nav.dataset.ready = 'true';
    const links = $$('a[href^="#"]', nav);
    const targets = links.map((a) => document.getElementById(a.getAttribute('href').slice(1))).filter(Boolean);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        links.forEach((a) => a.classList.toggle('is-current', a.getAttribute('href') === `#${entry.target.id}`));
      });
    }, { rootMargin: '-40% 0px -55% 0px' });
    targets.forEach((t) => io.observe(t));
  };

  /* ==========================================================
     Contact form: pre-select the topic from ?topic=
     ========================================================== */
  const initContact = (root = document) => {
    const select = $('[data-topic-select]', root);
    if (!select) return;
    const topic = new URLSearchParams(window.location.search).get('topic');
    if (!topic) return;
    const match = Array.from(select.options).find((o) => o.value.toLowerCase() === topic.toLowerCase());
    if (match) select.value = match.value;
    else {
      const opt = new Option(topic, topic, true, true);
      select.add(opt, 1);
    }
  };

  [initProductForms, initProduct, initProductGallery, initQuantity, initCart, initVideos, initGalleries, initLocalNav, initContact]
    .forEach((fn) => window.Theme.register(fn));
})();